// Needs you: every Interaction waiting on the curator, across all the hubs
// they curate, gathered by hub, the longest wait first, then "For you to
// read": the informs they haven't read, newest first. An inform waits on no
// one, so it never counts; showing it here sends its read mark, and it
// leaves the list, and the navigation its dot, on the next read. Each hub's
// Interactions link to its Agent activity page, where the curator replies.
// Studio reads them on sign-in, once a minute while the page is in view,
// and after a reply (App.tsx), from GET /api/v1/agent/interactions. See
// Design > UI, "Needs you", and Design > Exchange, "The hub agent's
// activity".
//
// NeedsYouBody takes what was read as props and holds no state, so each state
// is tested by calling it.

import { useEffect } from "preact/hooks";
import { Button, EmptyState, Notice, PageHeader } from "../../../../ui/src/index.ts";
import { interactionsByHub, type WaitingInteractionsResponse } from "../../domain/agent.ts";
import type { HubId } from "../../../../src/exchange/curator-api.ts";
import type { HubSummary } from "../../domain/types.ts";
import { loadWaitingInteractions, markShownRead } from "../actions.ts";
import { hubLabel } from "../hubData.ts";
import { Failed, Loading, Section } from "../parts.tsx";
import { href } from "../router.ts";
import { useStore, type Load } from "../store.ts";
import { InteractionCard, intentLinkOf } from "./Agent.tsx";

/** A hub's name as Studio shows it, and its summary where it has a toolset, by Hub ID. */
export type HubLookup = (hubId: HubId) => { label: string; hub?: HubSummary | undefined };

export function NeedsYouBody({ load, hubOf, retry }: { load: Load<WaitingInteractionsResponse> | null; hubOf: HubLookup; retry: () => void }) {
  if (!load || load.status === "loading") return <Loading label="Loading what needs you" />;
  if (load.status === "failed") return <Failed what="what needs you" error={load.error} retry={retry} />;
  const { unavailable, unread } = load.value;
  const groups = interactionsByHub(load.value.interactions);
  const missed = unavailable.length > 0 && <Notice tone="warning" title={`Studio couldn't check ${unavailable.length === 1 ? "one hub" : `${unavailable.length} hubs`}`}
    action={<Button size="sm" onClick={retry}>Try again</Button>}>
    {unavailable.map((id) => hubOf(id).label).join(", ")}: {unavailable.length === 1 ? "its hub agent" : "their hub agents"} didn't answer, so anything waiting there isn't shown.
  </Notice>;
  const toRead = unread.length > 0 && <Section title="For you to read" note="What your hub agents told you since you last looked. It asks nothing of you.">
    <div class="st-agent-interactions">{[...unread].sort((a, b) => b.interaction.at - a.interaction.at).map(({ hubId, interaction }) => {
      const { label, hub } = hubOf(hubId);
      return <div key={interaction.interactionId}><p class="st-note"><a href={href({ name: "agent", hub: hubId })}>{label}</a></p><InteractionCard interaction={interaction} intent={intentLinkOf(hub)} /></div>;
    })}</div>
  </Section>;
  if (groups.length === 0) return <>{missed}{toRead || <EmptyState>Nothing needs you right now. When a hub agent asks you something or suggests a change, it shows here.</EmptyState>}</>;
  return <>{missed}{groups.map(({ hubId, interactions: waiting }) => {
    const { label, hub } = hubOf(hubId);
    const activity = href({ name: "agent", hub: hubId });
    return <Section key={hubId} title={label} note={`${waiting.length === 1 ? "One thing waits" : `${waiting.length} things wait`} for your reply.`}
      action={<Button size="sm" href={activity}>Open Agent activity</Button>}>
      <div class="st-agent-interactions">{waiting.map((i) => <InteractionCard key={i.interactionId} interaction={i} intent={intentLinkOf(hub)} replyForm={<Button size="sm" variant="solid" href={activity}>Reply</Button>} />)}</div>
    </Section>;
  })}{toRead}</>;
}

export function NeedsYou() {
  const load = useStore((s) => s.waitingInteractions);
  const hubs = useStore((s) => s.hubs);
  // What "For you to read" shows is read: its marks go one request per hub,
  // and markInteractionsRead sends none it already sent.
  const shown = load?.status === "ok" ? load.value.unread : [];
  const shownKey = shown.map((u) => u.interaction.interactionId).join(" ");
  useEffect(() => { void markShownRead(shown); }, [shownKey]);
  const hubOf: HubLookup = (hubId) => {
    const summary = hubs?.status === "ok" ? hubs.value.hubs.find((h) => h.hub.id === hubId) : undefined;
    const record = summary?.hub ?? (hubs?.status === "ok" ? hubs.value.hubsWithoutToolset.find((r) => r.id === hubId) : undefined);
    // A hub registered since the hubs were read shows by its Hub ID until they're read again.
    return { label: record ? hubLabel(record.name, hubs) : hubId, hub: summary };
  };
  return <>
    <PageHeader title="Needs you" description="What your hub agents are waiting on you for, across all your hubs."
      action={<Button size="sm" disabled={load?.status === "loading"} onClick={() => void loadWaitingInteractions()}>Read again</Button>} />
    <NeedsYouBody load={load} hubOf={hubOf} retry={() => void loadWaitingInteractions()} />
  </>;
}
